/* eslint-disable react/display-name */
import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation, useTranslation } from 'react-i18next'
import { keyframes } from '@emotion/react'
import { getRestaurantProfile, updateTimings } from '../apollo'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import {
  Box, Button, IconButton, Paper, Stack, Typography,
  Switch, Snackbar, Alert, CircularProgress, Divider, Menu, MenuItem, Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import ClosuresPanel from '../components/Timings/ClosuresPanel'
import SaveIcon from '@mui/icons-material/Save'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

const GET_RESTAURANT_PROFILE = gql`${getRestaurantProfile}`
const UPDATE_TIMINGS = gql`${updateTimings}`

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI']
const WEEKEND = ['SAT', 'SUN']
const DAY_FULL = {
  MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday',
  FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday',
}

// Convert between "HH:mm" strings (how windows are stored) and dayjs objects
// (what MUI TimePicker uses).
const strToDayjs = (s) => (s ? dayjs(`2020-01-01T${s}`) : null)
const dayjsToStr = (d) => (d && d.isValid && d.isValid() ? d.format('HH:mm') : '')

// Design tokens for this page, exposed as CSS variables (var(--tm-*)) on the
// page root. Menus and tooltips render in a portal outside that root, so they
// read the raw values from TOKENS instead.
const TOKENS = {
  bg: '#F7F8FA',
  surface: '#FFFFFF',
  border: '#EAECF0',
  'border-strong': '#D0D5DD',
  hairline: '#F2F4F7',
  'row-hover': '#FAFBFC',
  text: '#101828',
  'text-2': '#344054',
  muted: '#667085',
  faint: '#98A2B3',
  accent: '#1e40af',
  'accent-hover': '#1e3a8a',
  'accent-soft': '#EFF4FF',
  'accent-soft-2': '#DBE6FE',
  'accent-tint': '#F5F8FF',
  ring: '0 0 0 3px rgba(30, 64, 175, 0.22)',
  open: '#059669',
  'open-dot': '#10B981',
  'open-soft': '#ECFDF5',
  closed: '#475467',
  rose: '#E11D48',
  'rose-soft': '#FFF1F2',
  'switch-off': '#8C95A6',
  'radius-card': '16px',
  'radius-ctl': '10px',
  'radius-pill': '999px',
  'shadow-xs': '0 1px 2px rgba(16, 24, 40, 0.05)',
  'shadow-card': '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)',
  'shadow-lift': '0 8px 20px -6px rgba(30, 64, 175, 0.45), 0 2px 4px -2px rgba(16, 24, 40, 0.1)',
  ease: '150ms cubic-bezier(0.2, 0.8, 0.2, 1)',
}
const cssVars = Object.fromEntries(Object.entries(TOKENS).map(([k, val]) => [`--tm-${k}`, val]))
const v = (k) => `var(--tm-${k})`

const TNUM = { fontFeatureSettings: '"tnum"', fontVariantNumeric: 'tabular-nums' }
const PAGE_WIDTH = { width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, boxSizing: 'border-box' }
const CARD = {
  bgcolor: v('surface'), border: `1px solid ${v('border')}`,
  borderRadius: v('radius-card'), boxShadow: v('shadow-card'),
}
const EYEBROW = { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: v('muted') }
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: none; }
`

// Display-only helpers for the duration label and the week timeline. They read
// the "HH:mm" window strings; a 23:59 close is drawn as midnight and a close at
// or before the open runs past midnight.
const toMinutes = (s) => {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s || '')
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}
const windowSpan = ([s, e]) => {
  const start = toMinutes(s)
  let end = toMinutes(e)
  if (start === null || end === null) return null
  if (end === 1439) end = 1440
  if (end <= start) end += 1440
  return [start, end]
}
const formatDuration = (range) => {
  const span = windowSpan(range)
  if (!span) return ''
  const mins = span[1] - span[0]
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`
}
const timelineSegments = (windows) => windows.flatMap(range => {
  const span = windowSpan(range)
  if (!span) return []
  const [start, end] = span
  return end > 1440 ? [[start, 1440], [0, end - 1440]] : [[start, end]]
})

const Timings = props => {
  const { t } = props
  const restaurantId = localStorage.getItem('restaurantId')

  const { data, error: errorQuery, loading: loadingQuery } = useQuery(GET_RESTAURANT_PROFILE, {
    variables: { id: restaurantId },
  })

  const initialTimes = useMemo(() => {
    const map = {}
    DAYS.forEach(d => { map[d] = [['00:00', '23:59']] })
    data?.restaurant?.openingTimes?.forEach(o => {
      map[o.day] = o.times.map(t => [`${t.startTime[0]}:${t.startTime[1]}`, `${t.endTime[0]}:${t.endTime[1]}`])
    })
    return map
  }, [data])

  const [times, setTimes] = useState(initialTimes)
  useEffect(() => { setTimes(initialTimes) }, [initialTimes])

  const setDayTimes = (day, next) => setTimes(prev => ({ ...prev, [day]: next }))

  // Quick-fill: copy one day's windows onto a set of target days. Windows are
  // deep-cloned so each day keeps its own array (editing one won't mutate others).
  const copyDayTo = (fromDay, targetDays) => {
    setTimes(prev => {
      const source = (prev[fromDay] || []).map(([s, e]) => [s, e])
      const next = { ...prev }
      targetDays.forEach(d => {
        if (d === fromDay) return
        next[d] = source.map(([s, e]) => [s, e])
      })
      return next
    })
    setSnack({ open: true, severity: 'success', msg: `Copied ${DAY_FULL[fromDay]} hours to ${targetDays.filter(d => d !== fromDay).length} day(s)` })
  }

  const [mutate, { loading }] = useMutation(UPDATE_TIMINGS)
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' })

  const handleSave = () => {
    const openingTimes = DAYS.map(day => ({
      day,
      times: (times[day] || []).map(([start, end]) => ({
        startTime: start.split(':').map(Number),
        endTime: end.split(':').map(Number),
      })),
    }))
    mutate({
      variables: { id: restaurantId, openingTimes },
      onCompleted: () => setSnack({ open: true, severity: 'success', msg: t('TimeSavedSuccessfully') || 'Timings saved successfully' }),
      onError: () => setSnack({ open: true, severity: 'error', msg: t('ErrorWhileSavingTime') || 'Error while saving timings' }),
    })
  }

  const openDaysCount = DAYS.filter(d => (times[d] || []).length > 0).length
  const closedDaysCount = 7 - openDaysCount
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  // Same clock as the header date; DAYS is Monday-first, getDay() is Sunday-first.
  const todayKey = DAYS[(new Date().getDay() + 6) % 7]
  const openFlags = DAYS.map(d => (times[d] || []).length > 0)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{
        ...cssVars,
        bgcolor: v('bg'), minHeight: '100vh', color: v('text'),
        '@media (prefers-reduced-motion: reduce)': {
          '& *, & *::before, & *::after': { transitionDuration: '0.01ms !important', animationDuration: '0.01ms !important' },
        },
      }}>
        {/* Header */}
        <Box component="header" sx={{
          position: 'relative', overflow: 'hidden',
          bgcolor: v('surface'), borderBottom: `1px solid ${v('border')}`,
          '&::before': {
            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(560px 220px at 8% -30%, rgba(30, 64, 175, 0.14), transparent 70%), radial-gradient(420px 200px at 96% -50%, rgba(30, 64, 175, 0.08), transparent 70%)',
          },
          '&::after': {
            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: 'radial-gradient(rgba(16, 24, 40, 0.07) 1px, transparent 1px)',
            backgroundSize: '16px 16px',
            maskImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent 90%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent 90%)',
          },
        }}>
          <Box sx={{ ...PAGE_WIDTH, py: { xs: 2.5, md: 3 }, position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
              display: 'grid', placeItems: 'center',
              bgcolor: v('accent-soft'), color: v('accent'), border: `1px solid ${v('accent-soft-2')}`,
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 1px 2px rgba(30, 64, 175, 0.08)',
            }}>
              <AccessTimeIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography component="h1" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 600, color: v('text'), lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                Business Hours
              </Typography>
              <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1} rowGap={0.5} sx={{ mt: 0.75 }}>
                <Box component="span" sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 24, px: 1.25,
                  borderRadius: v('radius-pill'), bgcolor: v('surface'), border: `1px solid ${v('border')}`,
                  boxShadow: v('shadow-xs'), color: v('text-2'), fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', ...TNUM,
                }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: v('muted') }} />
                  {dateStr}
                </Box>
                <Typography component="span" sx={{ fontSize: 14, color: v('muted') }}>
                  Set when customers can place orders
                </Typography>
              </Stack>
            </Box>
            <Button
              variant="contained"
              disableElevation
              startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              sx={{
                height: 40, px: 2, flexShrink: 0, width: { xs: '100%', sm: 'auto' },
                borderRadius: v('radius-ctl'), bgcolor: v('accent'), color: '#fff',
                textTransform: 'none', fontWeight: 600, fontSize: 14, letterSpacing: 0,
                boxShadow: `${TOKENS['shadow-xs']}, inset 0 1px 0 rgba(255, 255, 255, 0.12)`,
                transition: `transform ${v('ease')}, box-shadow ${v('ease')}, background-color ${v('ease')}`,
                '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
                '&:hover': { bgcolor: v('accent-hover'), transform: 'translateY(-1px)', boxShadow: v('shadow-lift') },
                '&:active': { transform: 'translateY(0)', boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)' },
                '&.Mui-focusVisible': { boxShadow: `${TOKENS['shadow-xs']}, ${TOKENS.ring}` },
                '&.Mui-disabled': { bgcolor: v('accent'), color: '#fff', opacity: 0.75 },
              }}
            >
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ ...PAGE_WIDTH, pt: 3, pb: 5 }}>
          {errorQuery && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: v('radius-card'), border: '1px solid #FECDD6', bgcolor: v('rose-soft') }}>
              <Typography sx={{ color: '#BE123C', fontSize: 14 }}>{errorQuery.message}</Typography>
            </Paper>
          )}

          {loadingQuery ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <CircularProgress sx={{ color: v('accent') }} />
            </Box>
          ) : (
            <>
              {/* Summary row */}
              <Box sx={{ display: 'grid', gap: 2, mb: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 2fr' } }}>
                <StatCard
                  label="Open Days"
                  value={openDaysCount}
                  valueColor={v('open')}
                  icon={EventAvailableOutlinedIcon}
                  iconColor={v('open')}
                  iconBg={v('open-soft')}
                  dots={openFlags}
                  dotColor={v('open-dot')}
                />
                <StatCard
                  label="Closed Days"
                  value={closedDaysCount}
                  valueColor={closedDaysCount > 0 ? v('rose') : v('closed')}
                  icon={EventBusyOutlinedIcon}
                  iconColor={closedDaysCount > 0 ? v('rose') : v('muted')}
                  iconBg={closedDaysCount > 0 ? v('rose-soft') : v('hairline')}
                  dots={openFlags.map(open => !open)}
                  dotColor={v('rose')}
                />
                <Paper elevation={0} sx={{
                  ...CARD, p: 2.5, display: 'flex', gap: 1.75, gridColumn: { sm: '1 / -1', md: 'auto' },
                  bgcolor: v('accent-tint'), borderColor: v('accent-soft-2'), boxShadow: 'none',
                }}>
                  <Box sx={{
                    width: 32, height: 32, flexShrink: 0, borderRadius: v('radius-ctl'),
                    display: 'grid', placeItems: 'center',
                    bgcolor: v('surface'), color: v('accent'), border: `1px solid ${v('accent-soft-2')}`, boxShadow: v('shadow-xs'),
                  }}>
                    <LightbulbOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...EYEBROW, color: v('accent') }}>Tip</Typography>
                    <Typography sx={{ color: v('text-2'), fontSize: 14, mt: 0.75, lineHeight: 1.65 }}>
                      Toggle a day off to mark it closed. Tap <Box component="span" sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.25, verticalAlign: 'middle',
                        height: 22, pl: 0.5, pr: 1, borderRadius: v('radius-pill'),
                        bgcolor: v('surface'), border: `1px solid ${v('accent-soft-2')}`,
                        color: v('accent'), fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                      }}><AddIcon sx={{ fontSize: 14 }} />Add window</Box> to add a second time range for split shifts (e.g. lunch & dinner).
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              <WeekTimeline times={times} todayKey={todayKey} />

              {/* Weekly hours */}
              <Box component="section" aria-label="Weekly hours" sx={{
                display: 'grid', gap: { xs: 1.5, sm: 0 },
                bgcolor: { sm: v('surface') },
                border: { sm: `1px solid ${v('border')}` },
                borderRadius: { sm: v('radius-card') },
                boxShadow: { sm: v('shadow-card') },
                overflow: { sm: 'hidden' },
              }}>
                {DAYS.map(day => (
                  <DayRow
                    key={day}
                    day={day}
                    isToday={day === todayKey}
                    times={times[day] || []}
                    onChange={(next) => setDayTimes(day, next)}
                    onCopyTo={(targetDays) => copyDayTo(day, targetDays)}
                  />
                ))}
              </Box>

              {/* Exceptions to the weekly hours above: holidays, vacation weeks,
                  a private event. Saved on their own, not by "Save Changes". */}
              <ClosuresPanel restaurantId={restaurantId} />
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  )
}

export default withTranslation()(Timings)

const StatCard = ({ label, value, valueColor, icon: Icon, iconColor, iconBg, dots, dotColor }) => (
  <Paper elevation={0} sx={{ ...CARD, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
      <Typography sx={EYEBROW}>{label}</Typography>
      <Box sx={{
        width: 32, height: 32, borderRadius: v('radius-ctl'), display: 'grid', placeItems: 'center',
        bgcolor: iconBg, color: iconColor, transition: `background-color ${v('ease')}, color ${v('ease')}`,
      }}>
        <Icon sx={{ fontSize: 18 }} />
      </Box>
    </Stack>
    <Stack direction="row" alignItems="baseline" gap={0.75}>
      <Typography sx={{ fontSize: 36, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em', color: valueColor, ...TNUM }}>
        {value}
      </Typography>
      <Typography sx={{ color: v('muted'), fontSize: 14, ...TNUM }}>/ 7 days</Typography>
    </Stack>
    <Stack direction="row" gap={0.75} aria-hidden sx={{ mt: 'auto' }}>
      {dots.map((on, i) => (
        <Box key={i} sx={{
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: on ? dotColor : '#E4E7EC', transition: `background-color ${v('ease')}`,
        }} />
      ))}
    </Stack>
  </Paper>
)

// Read-only picture of the week on a 24-hour track, drawn from the same
// windows the rows below edit. Hidden from screen readers: the rows carry
// the same information as text.
const AXIS = ['12 AM', '6 AM', '12 PM', '6 PM', '12 AM']
const WeekTimeline = ({ times, todayKey }) => (
  <Paper elevation={0} aria-hidden sx={{ ...CARD, px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5, mb: 2 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr)', columnGap: 2, rowGap: 1, alignItems: 'center' }}>
      {DAYS.map(day => {
        const windows = times[day] || []
        const isToday = day === todayKey
        return (
          <React.Fragment key={day}>
            <Typography sx={{ fontSize: 12, lineHeight: 1, fontWeight: isToday ? 700 : 500, color: isToday ? v('accent') : v('muted') }}>
              {(DAY_FULL[day] || day).slice(0, 3)}
            </Typography>
            <Box sx={{
              position: 'relative', height: 10, borderRadius: v('radius-pill'), overflow: 'hidden',
              bgcolor: v('hairline'),
              backgroundImage: windows.length === 0
                ? 'repeating-linear-gradient(135deg, transparent 0 4px, rgba(152, 162, 179, 0.35) 4px 5px)'
                : 'none',
              boxShadow: isToday ? `0 0 0 1px ${TOKENS['accent-soft-2']}` : 'none',
              // hairline ticks every six hours, drawn over the bars
              '&::after': {
                content: '""', position: 'absolute', inset: 0,
                backgroundImage: 'linear-gradient(90deg, transparent calc(25% - 1px), rgba(255, 255, 255, 0.9) calc(25% - 1px) 25%, transparent 25% calc(50% - 1px), rgba(255, 255, 255, 0.9) calc(50% - 1px) 50%, transparent 50% calc(75% - 1px), rgba(255, 255, 255, 0.9) calc(75% - 1px) 75%, transparent 75%)',
              },
            }}>
              {timelineSegments(windows).map(([a, b], k) => (
                <Box key={k} sx={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${(a / 1440) * 100}%`, width: `${((b - a) / 1440) * 100}%`,
                  borderRadius: v('radius-pill'), background: `linear-gradient(90deg, ${TOKENS['open-dot']}, ${TOKENS.open})`,
                  transition: `left ${v('ease')}, width ${v('ease')}`,
                }} />
              ))}
            </Box>
          </React.Fragment>
        )
      })}
      <Box />
      <Box sx={{ position: 'relative', height: 16 }}>
        {AXIS.map((label, i) => (
          <Typography key={i} component="span" sx={{
            position: 'absolute', left: `${i * 25}%`, top: 0,
            transform: i === 0 ? 'none' : i === AXIS.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
            fontSize: 11, color: v('muted'), whiteSpace: 'nowrap', ...TNUM,
          }}>
            {label}
          </Typography>
        ))}
      </Box>
    </Box>
  </Paper>
)

const SWITCH_SX = {
  width: 40, height: 24, p: 0, overflow: 'visible',
  '& .MuiSwitch-switchBase': {
    p: '3px', color: '#fff',
    transition: `transform ${v('ease')}`,
    '&:hover': { bgcolor: 'transparent' },
    '&.Mui-checked': {
      transform: 'translateX(16px)', color: '#fff',
      '&:hover': { bgcolor: 'transparent' },
      '& + .MuiSwitch-track': { bgcolor: v('open'), opacity: 1 },
    },
    '&.Mui-focusVisible + .MuiSwitch-track': { boxShadow: v('ring') },
  },
  '& .MuiSwitch-thumb': { width: 18, height: 18, boxShadow: '0 1px 2px rgba(16, 24, 40, 0.25)' },
  '& .MuiSwitch-track': {
    borderRadius: '12px', bgcolor: v('switch-off'), opacity: 1,
    transition: `background-color ${v('ease')}, box-shadow ${v('ease')}`,
  },
}

// One window = both time fields inside a single pill. The closed-day
// placeholder shares its width so the column keeps its rhythm.
const WINDOW_WIDTH = 244
const WINDOW_PILL_SX = {
  position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 0.5,
  height: 40, px: 0.25, boxSizing: 'border-box',
  width: { sm: WINDOW_WIDTH }, flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: 0,
  bgcolor: v('surface'), border: `1px solid ${v('border-strong')}`,
  borderRadius: v('radius-pill'), boxShadow: v('shadow-xs'),
  transition: `border-color ${v('ease')}, box-shadow ${v('ease')}`,
  '&:hover': { borderColor: v('faint') },
  '&:focus-within': { borderColor: v('accent'), boxShadow: v('ring') },
}
const TIME_FIELD_SX = {
  flex: '1 1 0', minWidth: 0,
  '& .MuiInputBase-root': { height: 34, borderRadius: v('radius-pill'), pr: 0.5, transition: `background-color ${v('ease')}` },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiInputBase-root.Mui-focused': { bgcolor: v('accent-soft') },
  '& .MuiInputBase-root.Mui-error': { bgcolor: v('rose-soft') },
  '& .MuiInputBase-root.Mui-error .MuiInputBase-input': { color: v('rose') },
  '& .MuiInputBase-input': {
    py: 0, pl: 1.5, pr: 0, fontSize: 15, fontWeight: 500, color: v('text'), letterSpacing: '-0.01em', ...TNUM,
  },
  '& .MuiInputAdornment-root': { ml: 0.25 },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    p: 0.5, color: v('faint'),
    '&:hover': { color: v('accent'), bgcolor: 'transparent' },
    '&.Mui-focusVisible': { boxShadow: v('ring') },
  },
  '& .MuiInputAdornment-root svg': { fontSize: 16 },
}
const GHOST_ICON_SX = {
  width: 36, height: 36, borderRadius: v('radius-ctl'), color: v('muted'),
  transition: `background-color ${v('ease')}, color ${v('ease')}, opacity ${v('ease')}`,
  '&:hover': { bgcolor: v('hairline'), color: v('text') },
  '&.Mui-focusVisible': { boxShadow: v('ring') },
  '&.Mui-disabled': { color: v('border-strong') },
}

const DayRow = ({ day, isToday, times, onChange, onCopyTo }) => {
  const { t } = useTranslation()
  const isClosed = times.length === 0
  const isOpenAllDay = times.length === 1 && times[0][0] === '00:00' && times[0][1] === '23:59'

  const [copyAnchor, setCopyAnchor] = useState(null)
  const openCopyMenu = (e) => setCopyAnchor(e.currentTarget)
  const closeCopyMenu = () => setCopyAnchor(null)
  const handleCopy = (targetDays) => { onCopyTo(targetDays); closeCopyMenu() }
  const isWeekday = WEEKDAYS.includes(day)

  const toggleClosed = (e) => {
    if (e.target.checked) onChange([['00:00', '23:59']])
    else onChange([])
  }

  const addWindow = () => onChange([...times, ['09:00', '17:00']])
  const removeWindow = (i) => onChange(times.filter((_, idx) => idx !== i))
  const updateWindow = (i, val) => {
    const next = [...times]
    next[i] = val
    onChange(next)
  }

  const dayName = DAY_FULL[day] || day
  const status = isClosed
    ? 'Closed'
    : isOpenAllDay
      ? 'Open 24h'
      : `Open · ${times.length} window${times.length > 1 ? 's' : ''}`

  return (
    <Box className="tm-day" sx={{
      position: 'relative',
      display: 'grid', alignItems: 'start', columnGap: 2, rowGap: 1.5,
      gridTemplateColumns: {
        xs: '40px minmax(0, 1fr)',
        sm: '40px minmax(0, 1fr) auto',
        lg: '40px 176px minmax(0, 1fr) auto',
      },
      gridTemplateAreas: {
        xs: '"toggle day" "windows windows" "actions actions"',
        sm: '"toggle day actions" ". windows windows"',
        lg: '"toggle day windows actions"',
      },
      px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 },
      bgcolor: isClosed ? '#FCFCFD' : v('surface'),
      border: { xs: `1px solid ${v('border')}`, sm: 'none' },
      borderRadius: { xs: v('radius-card'), sm: 0 },
      boxShadow: { xs: v('shadow-card'), sm: 'none' },
      transition: `background-color ${v('ease')}`,
      '&:hover': { bgcolor: v('row-hover') },
      '.tm-day + &': { borderTop: { sm: `1px solid ${v('hairline')}` } },
      // the remove-window button waits for hover or keyboard focus where a
      // pointer can hover; on touch screens it is always shown
      '@media (hover: hover)': {
        '& .tm-remove': { opacity: 0 },
        '&:hover .tm-remove, &:focus-within .tm-remove': { opacity: 1 },
      },
      ...(isToday && {
        '&::before': {
          content: '""', position: 'absolute', left: 0, top: 14, bottom: 14, width: 3,
          borderRadius: '0 3px 3px 0', bgcolor: v('accent'),
        },
      }),
    }}>
      <Box sx={{ gridArea: 'toggle', height: 40, display: 'flex', alignItems: 'center' }}>
        <Switch
          checked={!isClosed}
          onChange={toggleClosed}
          disableRipple
          inputProps={{ 'aria-label': `${dayName} open` }}
          sx={SWITCH_SX}
        />
      </Box>

      <Box sx={{ gridArea: 'day', minWidth: 0, minHeight: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography component="span" sx={{
            fontSize: 15, fontWeight: 600, lineHeight: 1.3,
            color: isClosed ? v('muted') : v('text'), transition: `color ${v('ease')}`,
          }}>
            {dayName}
          </Typography>
          {isToday && (
            <Box component="span" sx={{
              display: 'inline-flex', alignItems: 'center', height: 18, px: 0.75,
              borderRadius: v('radius-pill'), bgcolor: v('accent-soft'), border: `1px solid ${v('accent-soft-2')}`,
              color: v('accent'), fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              Today
            </Box>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
          <Box component="span" aria-hidden sx={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            bgcolor: isClosed ? v('faint') : v('open-dot'),
            boxShadow: isClosed ? 'none' : '0 0 0 3px rgba(16, 185, 129, 0.16)',
            transition: `background-color ${v('ease')}, box-shadow ${v('ease')}`,
          }} />
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3, color: v('muted'), ...TNUM }}>
            {status}
          </Typography>
        </Stack>
      </Box>

      {/* key restarts the fade whenever the day flips between open and closed */}
      <Box key={isClosed ? 'closed' : 'open'} sx={{ gridArea: 'windows', minWidth: 0, animation: `${fadeIn} 150ms ease-out` }}>
        {isClosed ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: 40, width: { xs: '100%', sm: WINDOW_WIDTH }, maxWidth: '100%', boxSizing: 'border-box',
            borderRadius: v('radius-pill'), border: `1px dashed ${v('border-strong')}`,
            color: v('muted'), fontSize: 13, fontWeight: 500,
          }}>
            Closed all day
          </Box>
        ) : (
          <Stack gap={1}>
            {times.map((range, i) => {
              const duration = formatDuration(range)
              return (
                <Stack key={i} direction="row" alignItems="center" gap={1} role="group" aria-label={`${dayName}, window ${i + 1}`}>
                  <Box sx={WINDOW_PILL_SX}>
                    <TimePicker
                      value={strToDayjs(range[0])}
                      onChange={(val) => updateWindow(i, [dayjsToStr(val), range[1]])}
                      slotProps={{ textField: { size: 'small', sx: TIME_FIELD_SX } }}
                    />
                    <ArrowForwardRoundedIcon sx={{ fontSize: 14, color: v('faint'), flexShrink: 0, mx: 0.25 }} />
                    <Box component="span" sx={SR_ONLY}>to</Box>
                    <TimePicker
                      value={strToDayjs(range[1])}
                      onChange={(val) => updateWindow(i, [range[0], dayjsToStr(val)])}
                      slotProps={{ textField: { size: 'small', sx: TIME_FIELD_SX } }}
                    />
                  </Box>
                  {duration && (
                    <Box component="span" sx={{
                      flexShrink: 0, px: 0.75, py: 0.25, borderRadius: v('radius-pill'), bgcolor: v('hairline'),
                      color: v('muted'), fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', ...TNUM,
                    }}>
                      {duration}
                    </Box>
                  )}
                  {times.length > 1 && (
                    <IconButton
                      className="tm-remove"
                      size="small"
                      aria-label={`Remove ${dayName} window ${i + 1}`}
                      onClick={() => removeWindow(i)}
                      sx={{
                        ...GHOST_ICON_SX, width: 32, height: 32, flexShrink: 0,
                        '&:hover': { bgcolor: v('rose-soft'), color: v('rose') },
                        '&.Mui-focusVisible': { boxShadow: v('ring'), opacity: 1 },
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>

      <Box sx={{
        gridArea: 'actions', minHeight: 40, display: 'flex', alignItems: 'center', gap: 0.5,
        justifyContent: { xs: 'space-between', sm: 'flex-end' },
        borderTop: { xs: `1px solid ${v('hairline')}`, sm: 'none' },
        pt: { xs: 1.5, sm: 0 },
      }}>
        {/* Copy this day's hours to other days */}
        <Tooltip title={isClosed ? 'Set hours first, then copy' : 'Copy these hours to other days'}>
          <span>
            <IconButton
              size="small"
              onClick={openCopyMenu}
              disabled={isClosed}
              aria-label="Copy these hours to other days"
              aria-haspopup="menu"
              sx={GHOST_ICON_SX}
            >
              <ContentCopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={copyAnchor}
          open={Boolean(copyAnchor)}
          onClose={closeCopyMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 0.75, minWidth: 240, borderRadius: '12px', border: `1px solid ${TOKENS.border}`,
              boxShadow: '0 12px 24px -6px rgba(16, 24, 40, 0.12), 0 4px 8px -4px rgba(16, 24, 40, 0.06)',
              '& .MuiMenuItem-root': { fontSize: 14, color: TOKENS['text-2'], mx: 0.5, borderRadius: '8px' },
            },
          }}
        >
          <MenuItem onClick={() => handleCopy(DAYS)}>Copy to all days</MenuItem>
          <MenuItem onClick={() => handleCopy(WEEKDAYS)}>Copy to weekdays (Mon–Fri)</MenuItem>
          <MenuItem onClick={() => handleCopy(WEEKEND)}>Copy to weekend (Sat–Sun)</MenuItem>
          <Divider />
          <MenuItem onClick={() => handleCopy(isWeekday ? WEEKDAYS : WEEKEND)}>
            {isWeekday ? 'Copy to other weekdays' : 'Copy to other weekend day'}
          </MenuItem>
        </Menu>
        {!isClosed && (
          <Button
            size="small"
            onClick={addWindow}
            startIcon={<AddIcon fontSize="small" />}
            sx={{
              height: 36, px: 1.5, borderRadius: v('radius-ctl'),
              textTransform: 'none', fontWeight: 600, fontSize: 14, color: v('accent'), whiteSpace: 'nowrap',
              transition: `background-color ${v('ease')}`,
              '& .MuiButton-startIcon': { mr: 0.5 },
              '&:hover': { bgcolor: v('accent-soft') },
              '&.Mui-focusVisible': { boxShadow: v('ring') },
            }}
          >
            Add window
          </Button>
        )}
      </Box>
    </Box>
  )
}
